import { tokens } from '@neo4j-ndl/base';
import { Button, IconButton, toast } from '@neo4j-ndl/react';
import { ChevronDownIconSolid } from '@neo4j-ndl/react/icons';
import { APP_SCOPE } from '@nx/constants';
import { createLogger } from '@nx/logger/legacy';
import {
  type InstanceSeamlessConnectionEnabled,
  type ProjectDefaultSeamlessConnectionEnabled,
  type ProjectSeamlessProfile,
  consoleApi,
  getApiError,
  getErrorMessage,
  isFetchBaseQueryError,
  useActiveOrg,
  useNotifications,
} from '@nx/state';
import { isNotNullish } from '@nx/stdlib';
import { isEqual } from 'lodash-es';
import type { ComponentPropsWithoutRef } from 'react';
import React, { useMemo, useState } from 'react';

import type { SeamlessConfigState } from '../settings';
import {
  DefaultSeamlessConnectionCheckbox,
  InstanceSeamlessConnectionCheckbox,
  MultiInstanceSeamlessConnectionCheckbox,
} from './checkboxes';
import './project-seamless-connection-settings.css';

type Props = {
  projectSeamlessProfile: ProjectSeamlessProfile;
  state: SeamlessConfigState;
  updateState: ({
    instances,
    projects,
  }: {
    instances?: InstanceSeamlessConnectionEnabled[];
    projects?: ProjectDefaultSeamlessConnectionEnabled[];
  }) => void;
} & ComponentPropsWithoutRef<'div'>;

export const ProjectSeamlessConnectionSettings = ({ projectSeamlessProfile, state, updateState, ...rest }: Props) => {
  const canExpand = projectSeamlessProfile.instances.length > 0;
  const [expanded, setExpanded] = useState<boolean>(true);

  const isEnabledForSome = projectSeamlessProfile.instances
    .map((instance) => state.instances[instance.id])
    .some((v) => v);
  const isEnabledForAll = projectSeamlessProfile.instances
    .map((instance) => state.instances[instance.id])
    .every((v) => v);

  return (
    <div {...rest}>
      <div className="project-table rounded-lg py-2">
        <div className={expanded ? 'header-expanded pb-2' : ''}>
          <div className="flex flex-row items-center justify-between px-2">
            <MultiInstanceSeamlessConnectionCheckbox
              id={projectSeamlessProfile.id}
              name={projectSeamlessProfile.name}
              checked={isEnabledForSome}
              indeterminate={isEnabledForSome && !isEnabledForAll}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (isNotNullish(projectSeamlessProfile)) {
                  const updatedInstances: InstanceSeamlessConnectionEnabled[] = projectSeamlessProfile.instances.map(
                    (instance) => ({
                      dbId: instance.id,
                      seamlessConnectionEnabled: instance.immutable
                        ? instance.seamlessConnectionEnabled
                        : e.target.checked,
                    }),
                  );

                  updateState({ instances: updatedInstances });
                }
              }}
              disabled={projectSeamlessProfile.instances.length === 0}
              className="mx-2 my-1"
            />
            <div className={`flex max-w-[265px] flex-row items-center ${!canExpand && 'pr-[52px]'}`}>
              <DefaultSeamlessConnectionCheckbox
                name={projectSeamlessProfile.name}
                id={projectSeamlessProfile.id}
                checked={state.projects[projectSeamlessProfile.id] ?? false}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateState({
                    projects: [
                      { projectId: projectSeamlessProfile.id, defaultSeamlessConnectionEnabled: e.target.checked },
                    ],
                  })
                }
                className="m-2"
              />
              {canExpand && (
                <IconButton
                  isClean
                  className="mx-2"
                  onClick={() => setExpanded((prev) => !prev)}
                  ariaLabel={expanded ? 'Contract' : 'Expand'}
                  isActive={false}
                  htmlAttributes={{
                    'data-testid': `expand-${projectSeamlessProfile.id}`,
                  }}
                >
                  <ChevronDownIconSolid
                    className="size-4"
                    style={{
                      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transitionDuration: tokens.transitions.values.duration.quick,
                    }}
                  />
                </IconButton>
              )}
            </div>
          </div>
        </div>
        {expanded &&
          projectSeamlessProfile.instances.map((instance) => (
            <InstanceSeamlessConnectionCheckbox
              id={instance.id}
              name={instance.name}
              immutable={instance.immutable}
              immutableReason={instance.immutableReason}
              checked={state.instances[instance.id] ?? false}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateState({ instances: [{ dbId: instance.id, seamlessConnectionEnabled: e.target.checked }] })
              }
              key={instance.id}
              className="mx-2 my-4 px-2"
            />
          ))}
      </div>
    </div>
  );
};

type NewProjectSeamlessConnectionSettingsProps = {
  projectSeamlessProfile: ProjectSeamlessProfile;
};

type NewProjectSeamlessConnectionSettingsState = {
  defaultSeamlessConnectionEnabled: boolean;
  instances: Record<string, boolean>;
};

export const NewProjectSeamlessConnectionSettings = ({
  projectSeamlessProfile,
}: NewProjectSeamlessConnectionSettingsProps) => {
  const activeOrg = useActiveOrg();
  const { addNotification } = useNotifications();

  const [updateOrganizationSeamlessProfile] = consoleApi.useUpdateOrganizationSeamlessProfileMutation();

  const canExpand = projectSeamlessProfile.instances.length > 0;
  const [expanded, setExpanded] = useState<boolean>(true);

  const initialState: NewProjectSeamlessConnectionSettingsState = useMemo(() => {
    const localState: NewProjectSeamlessConnectionSettingsState = {
      defaultSeamlessConnectionEnabled: projectSeamlessProfile.defaultSeamlessConnectionEnabled,
      instances: {},
    };

    projectSeamlessProfile.instances.forEach((instance) => {
      localState.instances[instance.id] = instance.seamlessConnectionEnabled;
    });

    return localState;
  }, [projectSeamlessProfile]);

  const [state, setState] = useState<NewProjectSeamlessConnectionSettingsState>(initialState);

  const isEnabledForSome = Object.values(state.instances).some((instance) => instance);
  const isEnabledForAll = Object.values(state.instances).every((instance) => instance);

  const handleInstanceChange = (instanceId: string, checked: boolean) => {
    setState((prev) => ({
      ...prev,
      instances: {
        ...prev.instances,
        [instanceId]: checked,
      },
    }));
  };

  const handleProjectChange = (checked: boolean) => {
    setState((prev) => ({
      ...prev,
      defaultSeamlessConnectionEnabled: checked,
    }));
  };

  const handleCancel = () => {
    setState(initialState);
  };

  const logger = createLogger(APP_SCOPE.framework);

  const handleSave = () => {
    const projects: ProjectDefaultSeamlessConnectionEnabled[] = [
      {
        projectId: projectSeamlessProfile.id,
        defaultSeamlessConnectionEnabled: state.defaultSeamlessConnectionEnabled,
      },
    ];
    const instances: InstanceSeamlessConnectionEnabled[] = Object.keys(state.instances).map((key) => ({
      dbId: key,
      seamlessConnectionEnabled: state.instances[key] ?? false,
    }));

    updateOrganizationSeamlessProfile({ organizationId: activeOrg.id, projects, instances })
      .unwrap()
      .then(() => {
        toast.success('Settings saved', {
          isCloseable: true,
          shouldAutoClose: true,
        });
      })
      .catch((error) => {
        const msg = isFetchBaseQueryError(error)
          ? getErrorMessage(getApiError(error))
          : `Our system is experiencing problems right now. Try again later or contact support if the problem persists.`;

        logger.error(msg);

        addNotification({
          type: 'danger',
          title: 'Failed to update tool authentication settings',
          description: msg,
          timeout: 5000,
        });
      });
  };
  const isChanged = useMemo(() => {
    return !isEqual(state, initialState);
  }, [state, initialState]);

  return (
    <div className="bg-neutral-bg-weak rounded-lg">
      <div className="project-table rounded-lg">
        <div className={expanded ? `header-expanded` : ''}>
          <div className="flex flex-row items-center justify-between gap-6 p-6">
            <MultiInstanceSeamlessConnectionCheckbox
              id={projectSeamlessProfile.id}
              name={projectSeamlessProfile.name}
              checked={isEnabledForSome}
              indeterminate={isEnabledForSome && !isEnabledForAll}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const updatedInstances = projectSeamlessProfile.instances.reduce<Record<string, boolean>>(
                  (acc, instance) => {
                    acc[instance.id] = instance.immutable ? instance.seamlessConnectionEnabled : e.target.checked;
                    return acc;
                  },
                  {},
                );

                setState((prev) => {
                  return {
                    ...prev,
                    instances: updatedInstances,
                  };
                });
              }}
              disabled={projectSeamlessProfile.instances.length === 0}
            />
            <div className={`flex flex-row items-center ${!canExpand && 'pr-[52px]'} gap-8`}>
              <DefaultSeamlessConnectionCheckbox
                name={projectSeamlessProfile.name}
                id={projectSeamlessProfile.id}
                checked={state.defaultSeamlessConnectionEnabled}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleProjectChange(e.target.checked)}
              />
              {canExpand && (
                <IconButton
                  isClean
                  onClick={() => setExpanded((prev) => !prev)}
                  ariaLabel={expanded ? 'Contract' : 'Expand'}
                  isActive={false}
                  htmlAttributes={{
                    'data-testid': `expand-${projectSeamlessProfile.id}`,
                  }}
                >
                  <ChevronDownIconSolid
                    style={{
                      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transitionDuration: tokens.transitions.values.duration.quick,
                    }}
                  />
                </IconButton>
              )}
            </div>
          </div>
        </div>
        {expanded && (
          <div className="flex flex-col gap-4 px-6 py-8">
            {projectSeamlessProfile.instances.map((instance) => (
              <InstanceSeamlessConnectionCheckbox
                id={instance.id}
                name={instance.name}
                immutable={instance.immutable}
                immutableReason={instance.immutableReason}
                checked={state.instances[instance.id] ?? false}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleInstanceChange(instance.id, e.target.checked)
                }
                key={instance.id}
              />
            ))}
          </div>
        )}
        <div className="border-palette-neutral-border-weak flex flex-row justify-end gap-4 border-t p-4">
          <Button
            size="small"
            color="neutral"
            fill="outlined"
            onClick={handleCancel}
            isDisabled={!isChanged}
            data-testid={`seamless-connection-project-cancel-${projectSeamlessProfile.id}`}
          >
            Cancel
          </Button>
          <Button
            size="small"
            fill="filled"
            onClick={handleSave}
            isDisabled={!isChanged}
            data-testid={`seamless-connection-project-submit-${projectSeamlessProfile.id}`}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};
